"use client"

import { useState, useEffect, useContext } from "react"
import {
  Box,
  TextField,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Button,
  Typography,
  InputAdornment,
  Chip,
  Tooltip,
  Card,
  CardContent,
  Divider,
  useTheme,
  alpha,
  TablePagination,
  CircularProgress,
  Alert,
  Fade,
} from "@mui/material"
import EditIcon from "@mui/icons-material/Edit"
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline"
import SearchIcon from "@mui/icons-material/Search"
import BusinessIcon from "@mui/icons-material/Business"
import PersonIcon from "@mui/icons-material/Person"
import EmailIcon from "@mui/icons-material/Email"
import PhoneIcon from "@mui/icons-material/Phone"
import BadgeIcon from "@mui/icons-material/Badge"
import RefreshIcon from "@mui/icons-material/Refresh"
import { ErpContext } from "../../context/ErpContext.js"
import ModalCrearActualizarEntidad from "./ModalCrearActualizarEntidad"

const TablaEntidadProveedor = () => {
  const theme = useTheme()
  const { getEntidad, isNavbarCollapsed, getTipoEntidad, tipoEntidades } = useContext(ErpContext)

  // Estados
  const [entidades, setEntidades] = useState([])
  const [filteredEntidades, setFilteredEntidades] = useState([])
  const [searchText, setSearchText] = useState("")
  const [openModal, setOpenModal] = useState(false)
  const [selectedEntidad, setSelectedEntidad] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(false)

  // Paginación
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)

  // Obtener Entidades y Tipos de Entidad
  useEffect(() => {
    fetchEntidades()
    if (tipoEntidades.length === 0) {
      getTipoEntidad()
    }
  }, [])

  const fetchEntidades = async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await getEntidad() // Obtener todas las entidades
      setEntidades(data || [])
      setFilteredEntidades(data || [])
    } catch (err) {
      console.error("Error al cargar Entidades:", err)
      setError("No se pudieron cargar las Entidades. Intente nuevamente.")
    } finally {
      setLoading(false)
    }
  }

  // Función helper para obtener el nombre del tipo de entidad
  const getTipoEntidadNombre = (idTipoEntidad) => {
    const tipo = tipoEntidades.find(t => t.id === idTipoEntidad)
    return tipo ? tipo.descripcion : "Desconocido"
  }

  // Función helper para obtener el color del tipo de entidad
  const getTipoEntidadColor = (idTipoEntidad) => {
    const tipo = tipoEntidades.find(t => t.id === idTipoEntidad)
    if (!tipo) return "default"
    
    // Mapeo dinámico basado en la descripción
    const descripcion = tipo.descripcion.toLowerCase()
    if (descripcion.includes("cliente")) return "primary"
    if (descripcion.includes("proveedor")) return "secondary" 
    if (descripcion.includes("empleado")) return "success"
    if (descripcion.includes("banco")) return "warning"
    if (descripcion.includes("local") || descripcion.includes("negocio")) return "info"
    if (descripcion.includes("pasajero")) return "error"
    return "default"
  }

  // Filtrar Entidades según la búsqueda
  useEffect(() => {
    if (!searchText) {
      setFilteredEntidades(entidades)
      return
    }

    const filtered = entidades.filter(
      (entidad) =>
        entidad.descripcion?.toLowerCase().includes(searchText.toLowerCase()) ||
        entidad.apellido?.toLowerCase().includes(searchText.toLowerCase()) ||
        entidad.dniCuitCuil?.toLowerCase().includes(searchText.toLowerCase()) ||
        entidad.email?.toLowerCase().includes(searchText.toLowerCase()),
    )

    setFilteredEntidades(filtered)
    setPage(0) // Resetear a primera página cuando se filtra
  }, [searchText, entidades])

  // Abrir modal para EDITAR entidad
  const handleOpenEditModal = (entidad) => {
    setSelectedEntidad(entidad) // Cargar datos en el modal
    setOpenModal(true)
  }

  // Abrir modal para CREAR entidad
  const handleOpenCreateModal = () => {
    setSelectedEntidad({
      id: null,
      descripcion: "",
      apellido: "",
      dniCuitCuil: "",
      telefono: "",
      email: "",
      direccion: "",
      localidad: "",
      provincia: "",
      idTipoEntidad: 1, // Tipo entidad por defecto
    })
    setOpenModal(true)
  }

  // Cerrar modal y actualizar lista si se guardaron cambios
  const handleCloseModal = async (refresh = false) => {
    setOpenModal(false)
    if (refresh) {
      await fetchEntidades()
      setSuccess(true)
      setTimeout(() => setSuccess(false), 3000)
    }
  }

  // Manejar cambio de página
  const handleChangePage = (event, newPage) => {
    setPage(newPage)
  }

  // Manejar cambio de filas por página
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10))
    setPage(0)
  }

  // Calcular Entidades para la página actual
  const paginatedEntidades = filteredEntidades.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)

  const tableStyles = {
    "&::-webkit-scrollbar": {
      width: "8px",
    },
    "&::-webkit-scrollbar-track": {
      background: "#f1f1f1",
    },
    "&::-webkit-scrollbar-thumb": {
      background: "#888",
      borderRadius: "4px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      background: "#555",
    },
  }

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 }, overflow: "hidden" }}>
      {/* Encabezado */}
      <Card
        elevation={2}
        sx={{
          mb: 3,
          borderRadius: 2,
          overflow: "visible",
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <BusinessIcon
              sx={{
                fontSize: 28,
                color: "#5b5b5f",
                mr: 1.5,
              }}
            />
            <Typography variant="h5" component="h1" sx={{ fontWeight: 600, color: "#5b5b5f" }}>
              Gestión de Entidades
            </Typography>
          </Box>

          <Divider sx={{ mb: 3 }} />

          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 2,
              alignItems: { xs: "stretch", sm: "center" },
              justifyContent: "space-between",
            }}
          >
            {/* Buscador */}
            <TextField
              placeholder="Buscar por nombre, apellido, CUIT o email..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              variant="outlined"
              size="small"
              fullWidth
              sx={{
                maxWidth: { sm: "60%", md: "40%" },
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />

            {/* Botones de acción */}
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<RefreshIcon />}
                onClick={fetchEntidades}
                disabled={loading}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  minWidth: 100,
                }}
              >
                Actualizar
              </Button>

              <Button
                variant="contained"
                startIcon={<AddCircleOutlineIcon />}
                onClick={handleOpenCreateModal}
                disabled={loading}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  boxShadow: 2,
                  minWidth: 140,
                  backgroundColor: "#5b5b5f",
                  "&:hover": {
                    backgroundColor: alpha("#5b5b5f", 0.8),
                  },
                }}
              >
                Nueva Entidad
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Alerta de éxito */}
      <Fade in={success}>
        <Alert
          severity="success"
          sx={{
            mb: 2,
            borderRadius: 2,
            boxShadow: 1,
          }}
          onClose={() => setSuccess(false)}
        >
          Operación completada con éxito
        </Alert>
      </Fade>

      {/* Alerta de error */}
      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 2,
            borderRadius: 2,
            boxShadow: 1,
          }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {/* Tabla de Clientes */}
      <Card
        elevation={2}
        sx={{
          borderRadius: 2,
          overflow: "hidden",
          height: "auto",
        }}
      >
        <Box sx={{ position: "relative" }}>
          {/* Overlay de carga */}
          {loading && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "rgba(255, 255, 255, 0.7)",
                zIndex: 10,
              }}
            >
              <CircularProgress />
            </Box>
          )}

          <TableContainer
            sx={{
              maxHeight: "calc(100vh - 300px)",
              overflowY: "auto",
              "&::-webkit-scrollbar": {
                width: "2px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#888",
                borderRadius: "10px",
              },
            }}
          >
            <Table stickyHeader sx={{ tableLayout: "fixed" }}>
              <TableHead>
                <TableRow sx={{ height: "45px" }}>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: "#5b5b5f",
                      color: "white",
                      width: "5%",
                      padding: "0 16px",
                      height: "45px !important",
                      lineHeight: "45px",
                    }}
                  >
                    ID
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: "#5b5b5f",
                      color: "white",
                      width: "15%",
                      padding: "0 16px",
                      height: "45px !important",
                      lineHeight: "45px",
                    }}
                  >
                    Nombre
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: "#5b5b5f",
                      color: "white",
                      width: "12%",
                      padding: "0 16px",
                      height: "45px !important",
                      lineHeight: "45px",
                    }}
                  >
                    Apellido
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: "#5b5b5f",
                      color: "white",
                      width: "12%",
                      padding: "0 16px",
                      height: "45px !important",
                      lineHeight: "45px",
                    }}
                  >
                    DNI / CUIT / CUIL
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: "#5b5b5f",
                      color: "white",
                      width: "11%",
                      padding: "0 16px",
                      height: "45px !important",
                      lineHeight: "45px",
                    }}
                  >
                    Tipo
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: "#5b5b5f",
                      color: "white",
                      width: "20%",
                      padding: "0 16px",
                      height: "45px !important",
                      lineHeight: "45px",
                    }}
                  >
                    Contacto
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: "#5b5b5f",
                      color: "white",
                      width: "15%",
                      padding: "0 16px",
                      height: "45px !important",
                      lineHeight: "45px",
                    }}
                  >
                    Ubicación
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: "#5b5b5f",
                      color: "white",
                      width: "10%",
                      padding: "0 16px",
                      height: "45px !important",
                      lineHeight: "45px",
                    }}
                  >
                    Acciones
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedEntidades.length > 0 ? (
                  paginatedEntidades.map((entidad) => (
                    <TableRow
                      key={entidad.id}
                      hover
                      sx={{
                        height: "35px",
                        "&:nth-of-type(even)": {
                          backgroundColor: "#F7F7F7",
                        },
                        "&:hover": {
                          backgroundColor: "#E3EAFD",
                          transition: "background 0.3s ease",
                        },
                        transition: "background-color 0.2s",
                      }}
                    >
                      <TableCell
                        sx={{
                          padding: "0 16px",
                          borderBottom: "1px solid #ccc",
                          height: "35px",
                        }}
                      >
                        <Chip
                          label={entidad.id}
                          size="small"
                          variant="outlined"
                          sx={{
                            fontWeight: 500,
                            minWidth: 40,
                            justifyContent: "center",
                          }}
                        />
                      </TableCell>
                      <TableCell
                        sx={{
                          padding: "0 16px",
                          borderBottom: "1px solid #ccc",
                          height: "35px",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <PersonIcon fontSize="small" sx={{ color: "#5b5b5f", mr: 1 }} />
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {entidad.descripcion || "—"}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{
                          padding: "0 16px",
                          borderBottom: "1px solid #ccc",
                          height: "35px",
                        }}
                      >
                        {entidad.apellido || "—"}
                      </TableCell>
                      <TableCell
                        sx={{
                          padding: "0 16px",
                          borderBottom: "1px solid #ccc",
                          height: "35px",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <BadgeIcon fontSize="small" sx={{ color: "#5b5b5f", mr: 1 }} />
                          <Typography variant="body2">{entidad.dniCuitCuil || "—"}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{
                          padding: "0 16px",
                          borderBottom: "1px solid #ccc",
                          height: "35px",
                        }}
                      >
                        <Chip
                          label={getTipoEntidadNombre(entidad.idTipoEntidad)}
                          size="small"
                          color={getTipoEntidadColor(entidad.idTipoEntidad)}
                          sx={{
                            fontWeight: 500,
                            fontSize: "0.75rem"
                          }}
                        />
                      </TableCell>
                      <TableCell
                        sx={{
                          padding: "0 16px",
                          borderBottom: "1px solid #ccc",
                          height: "35px",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 0.5,
                          }}
                        >
                          {entidad.email && (
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              <EmailIcon fontSize="small" sx={{ color: "#5b5b5f", mr: 1 }} />
                              <Typography variant="body2" noWrap>
                                {entidad.email}
                              </Typography>
                            </Box>
                          )}
                          {entidad.telefono && (
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              <PhoneIcon fontSize="small" sx={{ color: "#5b5b5f", mr: 1 }} />
                              <Typography variant="body2">{entidad.telefono}</Typography>
                            </Box>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{
                          padding: "0 16px",
                          borderBottom: "1px solid #ccc",
                          height: "35px",
                        }}
                      >
                        <Tooltip
                          title={`${entidad.direccion || ""} ${entidad.localidad || ""} ${entidad.provincia || ""}`}
                          arrow
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              maxWidth: 150,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {entidad.localidad ? `${entidad.localidad}, ${entidad.provincia || ""}` : "—"}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          padding: "0 16px",
                          borderBottom: "1px solid #ccc",
                          height: "35px",
                        }}
                      >
                        <Tooltip title="Editar entidad">
                          <IconButton
                            onClick={() => handleOpenEditModal(entidad)}
                            size="small"
                            sx={{
                              backgroundColor: "rgba(91, 91, 95, 0.1)",
                              color: "#5b5b5f",
                              "&:hover": {
                                backgroundColor: "rgba(91, 91, 95, 0.2)",
                              },
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                      {loading ? (
                        <Typography color="text.secondary">Cargando Entidades...</Typography>
                      ) : searchText ? (
                        <Typography color="text.secondary">
                          No se encontraron Entidades con ese criterio de búsqueda
                        </Typography>
                      ) : (
                        <Typography color="text.secondary">No hay Entidades registradas</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Paginación */}
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={filteredEntidades.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Filas por página:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            sx={{
              borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            }}
          />
        </Box>
      </Card>

      {/* Modal de Crear / Editar Entidad */}
      {openModal && (
        <ModalCrearActualizarEntidad
          open={openModal}
          handleClose={handleCloseModal}
          entidad={selectedEntidad}
          idTipoEntidad={selectedEntidad?.idTipoEntidad || 1}
        />
      )}
    </Box>
  )
}

export default TablaEntidadProveedor
